/**
 * Road Hazards Service - Real-time hazard reporting and polling
 * 
 * Features:
 * - Report hazards anonymously
 * - Poll for new hazards every 30 seconds
 * - Event-based notifications for new hazards
 * - Ghost mode for cop checkpoints
 */

export type HazardType = 'crash' | 'slowdown' | 'construction' | 'lane_closure' | 
                         'object_on_road' | 'flooded_road' | 'low_visibility' | 'dust_storm' | 'cop' | 'safety_inspector' | 'fire' |
                         'dot_officer' | 'personal_injury' | 'injured_worker' | 'truck_broke_down';

export type QuickHazardType = HazardType;

export interface RoadHazard {
  id: string;
  type: HazardType;
  latitude: number;
  longitude: number;
  note: string | null;
  reporter_id: string | null;
  reporter_name: string;
  votes: number;
  is_ghost: boolean;
  ghost_at: string | null;
  created_at: string;
  expires_at: string;
  location_context?: string | null;
  // Voting data
  still_there_votes?: number;
  gone_votes?: number;
  extension_applied?: boolean;
}

type HazardListener = (hazards: RoadHazard[], newHazards: RoadHazard[]) => void;

export interface HazardReportResult {
  success: boolean;
  hazard: RoadHazard | null;
  error?: string;
  message: string;
  status?: number;
}

export type HazardDeliveryMode = 'idle' | 'connecting' | 'streaming' | 'polling_fallback';

type HazardStatusListener = (mode: HazardDeliveryMode) => void;

const QUICK_HAZARD_META: Record<QuickHazardType, { note: string; label: string }> = {
  cop: { note: 'Police reported', label: 'police' },
  crash: { note: 'Crash reported', label: 'crash' },
  slowdown: { note: 'Traffic slowdown reported', label: 'slowdown' },
  construction: { note: 'Construction reported', label: 'construction' },
  lane_closure: { note: 'Lane closure reported', label: 'lane closure' },
  object_on_road: { note: 'Object on road reported', label: 'object on road' },
  safety_inspector: { note: 'Safety inspector reported', label: 'safety inspector' },
  dot_officer: { note: 'DOT officer reported', label: 'DOT officer' },
  fire: { note: 'Oilfield fire reported', label: 'fire' },
  flooded_road: { note: 'Flooded road reported', label: 'flood' },
  low_visibility: { note: 'Low visibility reported', label: 'low visibility' },
  dust_storm: { note: 'Dust storm reported', label: 'dust storm' },
  personal_injury: { note: 'Personal injury reported', label: 'personal injury' },
  injured_worker: { note: 'Injured worker reported', label: 'injured worker' },
  truck_broke_down: { note: 'Truck broke down reported', label: 'truck breakdown' },
};

export class RoadHazardsService {
  private hazards: RoadHazard[] = [];
  private listeners: Set<HazardListener> = new Set();
  private statusListeners: Set<HazardStatusListener> = new Set();
  private pollInterval: ReturnType<typeof setInterval> | null = null;
  private liveEventSource: EventSource | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private liveConsumerCount = 0;
  private deliveryMode: HazardDeliveryMode = 'idle';
  private apiUrl = '/api/hazards';
  private liveApiUrl = '/api/hazards/stream';

  private setDeliveryMode(mode: HazardDeliveryMode) {
    if (this.deliveryMode === mode) return;
    this.deliveryMode = mode;
    this.statusListeners.forEach((listener) => {
      try {
        listener(mode);
      } catch (error) {
        console.error('[Hazards] Status listener error:', error);
      }
    });
  }

  // Start polling for hazards
  startPolling(intervalMs: number = 30000) {
    if (this.pollInterval) return;
    
    // Fetch immediately
    this.fetchHazards();
    
    // Then poll at interval
    this.pollInterval = globalThis.setInterval(() => {
      this.fetchHazards();
    }, intervalMs);

    this.setDeliveryMode('polling_fallback');
    
    console.log('[Hazards] Started polling every', intervalMs / 1000, 'seconds');
  }

  // Stop polling
  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
      console.log('[Hazards] Stopped polling');
    }
    if (!this.liveEventSource && this.liveConsumerCount === 0) {
      this.setDeliveryMode('idle');
    }
  }

  startLiveUpdates(fallbackIntervalMs: number = 3000) {
    this.liveConsumerCount += 1;
    if (this.liveEventSource || this.deliveryMode === 'connecting') return;
    this.connectLiveStream(fallbackIntervalMs);
  }

  stopLiveUpdates() {
    this.liveConsumerCount = Math.max(0, this.liveConsumerCount - 1);
    if (this.liveConsumerCount > 0) return;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.liveEventSource) {
      this.liveEventSource.close();
      this.liveEventSource = null;
    }
    this.stopPolling();
    this.setDeliveryMode('idle');
  }

  subscribeStatus(listener: HazardStatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.deliveryMode);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  getDeliveryMode(): HazardDeliveryMode {
    return this.deliveryMode;
  }

  private connectLiveStream(fallbackIntervalMs: number) {
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
      this.startPolling(fallbackIntervalMs);
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.setDeliveryMode('connecting');
    const stream = new EventSource(this.liveApiUrl, { withCredentials: true });
    this.liveEventSource = stream;

    const handleSnapshot = (event: MessageEvent<string>) => {
      try {
        const payload = JSON.parse(event.data) as { hazards?: RoadHazard[] };
        if (!Array.isArray(payload.hazards)) return;
        this.applyHazardSnapshot(payload.hazards);
      } catch (error) {
        console.error('[Hazards] Stream snapshot parse error:', error);
      }
    };

    stream.addEventListener('snapshot', handleSnapshot as EventListener);
    stream.onopen = () => {
      this.setDeliveryMode('streaming');
      this.stopPolling();
      console.log('[Hazards] Live stream connected');
    };
    stream.onerror = () => {
      console.warn('[Hazards] Live stream failed, falling back to polling');
      stream.close();
      if (this.liveEventSource === stream) {
        this.liveEventSource = null;
      }
      if (this.liveConsumerCount > 0) {
        this.startPolling(fallbackIntervalMs);
        if (!this.reconnectTimeout) {
          this.reconnectTimeout = globalThis.setTimeout(() => {
            this.reconnectTimeout = null;
            if (this.liveConsumerCount > 0 && !this.liveEventSource) {
              this.connectLiveStream(fallbackIntervalMs);
            }
          }, 8000);
        }
      } else {
        this.setDeliveryMode('idle');
      }
    };
  }

  // Subscribe to hazard updates
  subscribe(listener: HazardListener): () => void {
    this.listeners.add(listener);
    
    // Immediately notify with current hazards
    if (this.hazards.length > 0) {
      listener(this.hazards, []);
    }
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Get current hazards
  getHazards(): RoadHazard[] {
    return [...this.hazards];
  }

  private applyHazardSnapshot(nextHazards: RoadHazard[]) {
    const oldIds = new Set(this.hazards.map((hazard) => hazard.id));
    const newHazards = nextHazards.filter((hazard) => !oldIds.has(hazard.id));
    this.hazards = nextHazards;
    this.notifyListeners(newHazards);
  }

  // Fetch hazards from API
  async fetchHazards(): Promise<RoadHazard[]> {
    try {
      const response = await fetch(this.apiUrl, { credentials: 'include' });
      const data = await response.json();
      
      if (data.success && Array.isArray(data.hazards)) {
        this.applyHazardSnapshot(data.hazards);
        return this.hazards;
      }
    } catch (error) {
      console.error('[Hazards] Fetch error:', error);
    }
    
    return this.hazards;
  }

  // Report a new hazard
  async reportHazard(
    type: HazardType,
    latitude: number,
    longitude: number,
    note?: string,
    reporterId?: string,
    reporterName?: string
  ): Promise<RoadHazard | null> {
    const result = await this.reportHazardDetailed(
      type,
      latitude,
      longitude,
      note,
      reporterId,
      reporterName,
    );
    return result.hazard;
  }

  async reportHazardDetailed(
    type: HazardType,
    latitude: number,
    longitude: number,
    note?: string,
    reporterId?: string,
    reporterName?: string
  ): Promise<HazardReportResult> {
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          type, 
          latitude, 
          longitude,
          note: note || null,
          reporterId: reporterId || null,
          reporterName: reporterName || 'Anonymous'
        }),
      });

      const data = await response.json().catch(() => null);

      if (data.success && data.hazard) {
        // Add to local list immediately
        this.hazards = [data.hazard, ...this.hazards];
        this.notifyListeners([data.hazard]);
        
        console.log('[Hazards] Reported:', type, 'at', latitude, longitude);
        return {
          success: true,
          hazard: data.hazard,
          message: `${type} reported`,
          status: response.status,
        };
      } else {
        const error = data?.error || `Request failed with status ${response.status}`;
        console.error('[Hazards] Report failed:', error);
        return {
          success: false,
          hazard: null,
          error,
          message: error,
          status: response.status,
        };
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('[Hazards] Report error:', error);
      return {
        success: false,
        hazard: null,
        error: message,
        message,
      };
    }

    return {
      success: false,
      hazard: null,
      error: 'Unknown hazard reporting error',
      message: 'Unknown hazard reporting error',
    };
  }

  async reportQuickHazard(
    type: QuickHazardType,
    latitude: number,
    longitude: number,
    reporterId?: string,
    reporterName?: string,
  ): Promise<HazardReportResult & { label: string; note: string }> {
    const meta = QUICK_HAZARD_META[type];
    const result = await this.reportHazardDetailed(
      type,
      latitude,
      longitude,
      meta.note,
      reporterId,
      reporterName,
    );

    if (result.success) {
      try {
        await this.fetchHazards();
      } catch (error) {
        console.warn('[Hazards] Quick hazard refresh failed after report:', error);
      }
      return {
        ...result,
        label: meta.label,
        note: meta.note,
        message: `Reported ${meta.label}.`,
      };
    }

    return {
      ...result,
      label: meta.label,
      note: meta.note,
      message: `Failed to report ${meta.label}: ${result.error || 'Unknown error'}`,
    };
  }

  // Vote on a hazard (still_there or gone)
  async voteOnHazard(
    hazardId: string,
    voteType: 'still_there' | 'gone',
    userId: string,
    userLatitude?: number,
    userLongitude?: number
  ): Promise<{ success: boolean; message: string; hazardRemoved?: boolean; ghostMode?: boolean; error?: string }> {
    try {
      const response = await fetch(`${this.apiUrl}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          hazardId,
          voteType,
          userId,
          userLatitude,
          userLongitude,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        // If hazard was removed, update local list
        if (data.hazardRemoved) {
          this.hazards = this.hazards.filter(h => h.id !== hazardId);
          this.notifyListeners([]);
        } else {
          // Update vote counts locally
          this.hazards = this.hazards.map(h => 
            h.id === hazardId 
              ? { 
                  ...h, 
                  still_there_votes: data.stillThereVotes,
                  gone_votes: data.goneVotes,
                  expires_at: data.expiresAt || h.expires_at,
                  extension_applied: data.timerExtended || h.extension_applied,
                }
              : h
          );
          this.notifyListeners([]);
        }
        
        console.log('[Hazards] Vote recorded:', voteType, 'on', hazardId);
        return { success: true, message: data.message, hazardRemoved: data.hazardRemoved, ghostMode: data.ghostMode };
      } else {
        console.warn('[Hazards] Vote failed:', data.error);
        return { success: false, message: data.error || 'Vote failed', error: data.error };
      }
    } catch (error) {
      console.error('[Hazards] Vote error:', error);
      return { success: false, message: 'Network error', error: String(error) };
    }
  }

  // Notify all listeners
  private notifyListeners(newHazards: RoadHazard[]) {
    this.listeners.forEach(listener => {
      try {
        listener(this.hazards, newHazards);
      } catch (error) {
        console.error('[Hazards] Listener error:', error);
      }
    });
  }

  // Get hazard icon info
  static getHazardInfo(type: HazardType, isGhost?: boolean): { 
    color: string; 
    emoji: string;
    label: string;
    expiresIn: string;
    durationMinutes: number;
  } {
    // Ghost cop has different display
    if (type === 'cop' && isGhost) {
      return { color: '#64748b', emoji: '👻', label: 'Checkpoint', expiresIn: '30 min', durationMinutes: 30 };
    }
    
    switch (type) {
      case 'crash':
        return { color: '#ef4444', emoji: '💥', label: 'Crash', expiresIn: '2 hours', durationMinutes: 120 };
      case 'slowdown':
        return { color: '#f59e0b', emoji: '🐢', label: 'Slowdown', expiresIn: '1 hour', durationMinutes: 60 };
      case 'construction':
        return { color: '#f97316', emoji: '🚧', label: 'Road Work', expiresIn: '24 hours', durationMinutes: 1440 };
      case 'lane_closure':
        return { color: '#8b5cf6', emoji: '🚫', label: 'Road Closed', expiresIn: '24 hours', durationMinutes: 1440 };
      case 'object_on_road':
        return { color: '#eab308', emoji: '⚠️', label: 'Debris', expiresIn: '2 hours', durationMinutes: 120 };
      case 'flooded_road':
        return { color: '#0ea5e9', emoji: '🌊', label: 'Flooded', expiresIn: '8 hours', durationMinutes: 480 };
      case 'low_visibility':
        return { color: '#64748b', emoji: '🌫️', label: 'Low Visibility', expiresIn: '1 hour', durationMinutes: 60 };
      case 'dust_storm':
        return { color: '#d97706', emoji: '🌪️', label: 'Dust Storm', expiresIn: '4 hours', durationMinutes: 240 };
      case 'cop':
        return { color: '#3b82f6', emoji: '🚔', label: 'Cop', expiresIn: '30 min', durationMinutes: 30 };
      case 'safety_inspector':
        return { color: '#f97316', emoji: '🛡️', label: 'Safety Inspector', expiresIn: '30 min', durationMinutes: 30 };
      case 'fire':
        return { color: '#f97316', emoji: '🔥', label: 'Fire', expiresIn: '4 hours', durationMinutes: 240 };
      case 'dot_officer':
        return { color: '#020617', emoji: 'DOT', label: 'DOT Officer', expiresIn: '30 min', durationMinutes: 30 };
      case 'personal_injury':
        return { color: '#ef4444', emoji: '🚑', label: 'Personal Injury', expiresIn: '4 hours', durationMinutes: 240 };
      case 'injured_worker':
        return { color: '#f43f5e', emoji: '🏥', label: 'Injured Worker', expiresIn: '4 hours', durationMinutes: 240 };
      case 'truck_broke_down':
        return { color: '#f97316', emoji: '🚛', label: 'Truck Broke Down', expiresIn: '4 hours', durationMinutes: 240 };
      default:
        return { color: '#f59e0b', emoji: '⚠️', label: 'Hazard', expiresIn: '1 hour', durationMinutes: 60 };
    }
  }
}

// Singleton instance
export const roadHazardsService = new RoadHazardsService();
